<template>
  <div class="nx-page">
    <!-- 导航栏 - 独立容器 -->
    <NxNav ref="nav" :config="config" />

    <!-- 主要内容区域 - 独立容器 -->
    <div class="nx-homepage">
      <!-- 英雄区轮播 -->
      <div id="home">
        <NxHero :items="carouselItems" :interval="carouselInterval" />
      </div>

      <!-- 关于文信 -->
      <div id="about">
        <NxAbout :config="config" />
      </div>

      <!-- 产品介绍 -->
      <div id="products">
        <NxProducts :config="config" />
        <NxScenarios :config="config" />
      </div>

      <!-- 经典案例 -->
      <div id="cases">
        <NxCases :config="config" @open-case="openCase" />
      </div>

      <!-- 服务领域 -->
      <div id="services">
        <NxServices :config="config" />
      </div>

      <!-- 知识产权 -->
      <div id="qualifications">
        <NxQualifications :config="config" @open-qualification="openQualification" />
      </div>

      <!-- 不同领域、不同部门应用 -->
      <div id="solutions">
        <NxDepartments :config="config" />
      </div>

      <!-- 合作伙伴/客户展示 -->
      <div id="partners">
        <NxCustomers :config="config" />
      </div>

      <!-- 页脚 -->
      <div id="contact">
        <NxFooter :config="config" />
      </div>
    </div>

    <!-- 回到顶部按钮 -->
    <button
      class="back-to-top"
      :class="{ 'back-to-top-visible': showBackToTop }"
      @click="scrollToTop"
      title="回到顶部"
    >
      <i class="el-icon-top"></i>
    </button>

    <!-- 图片查看器 -->
    <PictureView
      v-if="showViewer"
      :on-close="closeViewer"
      :url-list="srcList"
      :showpage="true"
      :case-mode="viewerCaseMode"
      :qualification-mode="viewerQualificationMode"
    />
  </div>
</template>

<script>
import "@/assets/css/next-tech-theme.less";
import NxNav from "@/components/next/NxNav.vue";
import NxHero from "@/components/next/NxHero.vue";
import NxServices from "@/components/next/NxServices.vue";
import NxProducts from "@/components/next/NxProducts.vue";
import NxScenarios from "@/components/next/NxScenarios.vue";
import NxCases from "@/components/next/NxCases.vue";
import NxAbout from "@/components/next/NxAbout.vue";
import NxCustomers from "@/components/next/NxCustomers.vue";
import NxFooter from "@/components/next/NxFooter.vue";
import NxDepartments from "@/components/next/NxDepartments.vue";
import NxQualifications from "@/components/next/NxQualifications.vue";
import PictureView from "@/components/PictureView.vue";
import { getConfig } from "@/utils/config.js";

export default {
  name: "NxHome",
  components: {
    NxNav,
    NxHero,
    NxServices,
    NxProducts,
    NxScenarios,
    NxCases,
    NxAbout,
    NxCustomers,
    NxFooter,
    NxDepartments,
    NxQualifications,
    PictureView,
  },
  data() {
    return {
      config: null,
      carouselItems: [],
      carouselInterval: 5000,
      caseList: [],
      showViewer: false,
      srcList: [],
      viewerCaseMode: false,
      viewerQualificationMode: false,
      showBackToTop: false,
      scrollRAF: null,
      sectionOffsets: {},
      scrollObserver: null
    };
  },
  async mounted() {
    // 页面加载时确保滚动到顶部
    window.scrollTo(0, 0);

    // 禁用浏览器自动滚动恢复
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // 加载配置
    this.config = await getConfig();

    // 从配置加载轮播图
    if (this.config?.carousel?.items) {
      this.carouselItems = this.config.carousel.items;
      this.carouselInterval = this.config.carousel.interval || 5000;
    }

    // 从配置加载案例
    if (this.config?.cases?.items) {
      this.caseList = this.config.cases.items.map(item => ({
        id: item.id,
        title: item.title,
        content: item.content,
        srcList: item.images.map(img => ({
          img: typeof img === 'string' ? img : img.url,
          info: [{ label: "info", value: "" }]
        }))
      }));
    }

    // 添加滚动动画
    this.setupScrollAnimations();

    // 缓存板块位置信息
    this.cacheSectionOffsets();

    // 监听滚动事件（使用 RAF 优化）
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // 添加 resize 监听（防抖）
    window.addEventListener('resize', this.handleResize, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    // 清理 IntersectionObserver
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }

    // 取消待执行的 RAF
    if (this.scrollRAF) {
      cancelAnimationFrame(this.scrollRAF);
    }
  },
  methods: {
    openCase(id) {
      const caseData = this.caseList.find(c => c.id === id);
      // 无图片的案例不打开图片查看器
      if (caseData && caseData.srcList && caseData.srcList.length > 0) {
        this.showViewer = true;
        this.srcList = caseData.srcList;
        this.viewerCaseMode = true;
        this.viewerQualificationMode = false;
      }
    },
    openQualification(imagePath) {
      if (imagePath) {
        this.showViewer = true;
        this.srcList = [{
          img: imagePath,
          info: [{ label: "info", value: "" }]
        }];
        this.viewerCaseMode = false;
        this.viewerQualificationMode = true;
      }
    },
    closeViewer() {
      this.showViewer = false;
    },
    cacheSectionOffsets() {
      const sections = ['home', 'about', 'products', 'cases', 'solutions', 'contact'];
      sections.forEach(section => {
        const element = document.querySelector(`#${section}`);
        if (element) {
          this.sectionOffsets[section] = element.offsetTop;
        }
      });
    },
    setupScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      this.scrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('nx-visible');
            // 动画完成后停止观察该元素
            this.scrollObserver.unobserve(entry.target);
          }
        });
      }, observerOptions);

      // 使用 requestAnimationFrame 确保在下一帧执行
      requestAnimationFrame(() => {
        document.querySelectorAll('.nx-animate-on-scroll, .nx-animate-left, .nx-animate-right, .nx-animate-scale, .nx-animate-rotate').forEach(el => {
          this.scrollObserver.observe(el);
        });
      });
    },
    handleResize() {
      // 防抖处理 resize
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.cacheSectionOffsets();
      }, 150);
    },
    handleScroll() {
      // 使用 requestAnimationFrame 优化滚动性能
      if (this.scrollRAF) {
        return;
      }

      this.scrollRAF = requestAnimationFrame(() => {
        this.scrollRAF = null;

        // 控制回到顶部按钮显示/隐藏
        this.showBackToTop = window.scrollY > 300;
      });
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  }
};
</script>

<style lang="less" scoped>
.nx-page {
  width: 100%;
  min-width: 320px;
}

.nx-homepage {
  width: 100%;
  min-width: 320px;
  overflow-x: hidden;
  background: linear-gradient(180deg, #e3f2fd 0%, #f0f7ff 50%, #e8f4fc 100%);
  background-image:
    radial-gradient(ellipse at 20% 20%, rgba(21, 101, 192, 0.08) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, rgba(0, 188, 212, 0.06) 0%, transparent 50%);
  position: relative;

  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      linear-gradient(90deg, rgba(21, 101, 192, 0.03) 1px, transparent 1px),
      linear-gradient(rgba(21, 101, 192, 0.03) 1px, transparent 1px);
    background-size: 80px 80px;
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }
}

/* 回到顶部按钮 */
.back-to-top {
  position: fixed;
  bottom: 40px;
  right: 40px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.4);
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 0;
  visibility: hidden;
  transform: translateY(20px);
  z-index: 999;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(135deg, #42a5f5 0%, #1976d2 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
}

.back-to-top-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.back-to-top:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(25, 118, 210, 0.5);

  &::before {
    opacity: 1;
  }
}

.back-to-top:active {
  transform: translateY(-2px);
}

.back-to-top i {
  font-size: 24px;
  color: #fff;
  font-weight: bold;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .back-to-top {
    bottom: 20px;
    right: 20px;
    width: 48px;
    height: 48px;
  }

  .back-to-top i {
    font-size: 20px;
  }
}
</style>
